import isEmpty from 'lodash/isEmpty'
import unescape from 'lodash/unescape'
import { ageGroupServed, getAgeGroupsFromProvider } from 'app/Bookings/utils'
import { ParsedUrlQuery } from 'querystring'
import { groupFilters } from 'app/ProviderSearch/utils'
import {
  practitionerTypes,
  providerProfileNumberWithCode,
  RudderstackIntegrations,
  sessionStorageKeys,
} from 'constants/common'
import { providerFilterKeyMapping } from 'dataSource/ProviderSearch'
import { FILTER_KEY_MAPPING } from 'app/ProviderSearch/constants'
import logEvent from 'utils/logEvent'
import { RudderEvent } from 'constants/rudderEvents'
import { convertHtmlToString, getPractitionerType } from 'utils/common'
import credentials from 'common/ProviderCredentials/credentials'
import { stateAbbreviations } from '../SeFriendly/validateUsaState'
import {
  THRIVEWORKS_SCHEMA_ORGANIZATION_ID,
  THRIVEWORKS_SCHEMA_WEBSITE_ID,
} from '../SeFriendly/constants'
import { formatProviderPronouns } from 'utils/formatProviderPronouns'
import { SessionOfferingType, ProviderServiceType } from './constants'

const DEFAULT_TESTIMONIAL_AUTHOR = 'A Thriveworks Client'
const TESTIMONIAL_RATING_SCALE = 5
const DEFAULT_PROVIDER_IMAGE =
  'https://thriveworks.com/images/default_provider.svg'

/** Display language filter values → BCP-47 / ISO language codes. */
const LANGUAGE_NAME_TO_ISO_CODE: Record<string, string> = {
  arabic: 'ar',
  armenian: 'hy',
  asl: 'ase',
  bengali: 'bn',
  bosnian: 'bs',
  cantonese: 'yue',
  creole: 'ht',
  croatian: 'hr',
  dutch: 'nl',
  english: 'en',
  farsi: 'fa',
  filipino: 'fil',
  french: 'fr',
  german: 'de',
  greek: 'el',
  gujarati: 'gu',
  hebrew: 'he',
  hindi: 'hi',
  hmong: 'hmn',
  hungarian: 'hu',
  irish: 'ga',
  italian: 'it',
  japanese: 'ja',
  korean: 'ko',
  mandarin: 'zh',
  persian: 'fa',
  polish: 'pl',
  portuguese: 'pt',
  punjabi: 'pa',
  romanian: 'ro',
  russian: 'ru',
  serbian: 'sr',
  sinhalese: 'si',
  spanish: 'es',
  swedish: 'sv',
  tagalog: 'tl',
  telugu: 'te',
  turkish: 'tr',
  ukrainian: 'uk',
  urdu: 'ur',
  vietnamese: 'vi',
  welsh: 'cy',
  yiddish: 'yi',
}

const IMAGE_EXTENSION_TO_ENCODING: Record<string, string> = {
  jpg: 'image/jpeg',
  jpeg: 'image/jpeg',
  png: 'image/png',
  gif: 'image/gif',
  webp: 'image/webp',
  svg: 'image/svg+xml',
}

export function sanitizeTextForJsonLd(text: string): string {
  // lodash/unescape cannot decode &#x27; (hexadecimal) for apostrophes
  return unescape(convertHtmlToString(text))
    .replace(/&#x27;/g, "'")
    .trim()
}

export function mapLanguageNameToIsoCode(
  languageName: string
): string | undefined {
  if (!languageName) {
    return undefined
  }

  return LANGUAGE_NAME_TO_ISO_CODE[languageName.trim().toLowerCase()]
}

export type ProviderPathKnowsLanguageEntry =
  | string
  | { '@type': 'Language'; name: string }

/** ISO codes when mapped; Language nodes for unmapped display names. */
export const buildProviderPathKnowsLanguage = (
  languagesSpoken: { value?: string }[] | undefined
): ProviderPathKnowsLanguageEntry[] => {
  const result: ProviderPathKnowsLanguageEntry[] = []
  const seenIsoCodes = new Set<string>()
  const seenUnmappedNames = new Set<string>()

  for (const language of languagesSpoken ?? []) {
    const name = language.value?.trim()

    if (!name) {
      continue
    }

    const isoCode = mapLanguageNameToIsoCode(name)

    if (isoCode) {
      if (!seenIsoCodes.has(isoCode)) {
        seenIsoCodes.add(isoCode)
        result.push(isoCode)
      }

      continue
    }

    const unmappedKey = name.toLowerCase()

    if (!seenUnmappedNames.has(unmappedKey)) {
      seenUnmappedNames.add(unmappedKey)
      result.push({
        '@type': 'Language',
        name,
      })
    }
  }

  return result
}

export const monthNames = {
  1: 'January',
  2: 'February',
  3: 'March',
  4: 'April',
  5: 'May',
  6: 'June',
  7: 'July',
  8: 'August',
  9: 'September',
  10: 'October',
  11: 'November',
  12: 'December',
}

export const providerGlobalFilters = {
  CounselingApproach: 'counseling_approach',
  Language: 'language',
  AreaOfConcern: 'area_of_concern',
}

export const getDataFromProviderFilters = (
  filters: ProviderFilter[],
  type: string
): ProviderFilter[] => {
  return filters?.filter((filter) => filter.type === type)
}

export const getInsuranceDetails = (
  providerDetails: Provider
): {
  acceptsInsurance: boolean
  providerInsurances: ProviderInsurance[]
} => {
  const practice = providerDetails?.practice
  const acceptsInsurance = !isEmpty(practice?.insurances)
  const providerInsurances = providerDetails?.practice?.insurances

  return {
    acceptsInsurance,
    providerInsurances,
  }
}

type ProviderSectionDetailsType = {
  genderPronouns: ProviderFilter[]
  populationServed: ProviderFilter[]
  acceptsInsurance: boolean
  location: string
  topSpecialities: ProviderFilter[]
  otherSpecialities: ProviderFilter[]
  availabilityTags: ProviderFilter[]
  agesServed: ProviderFilter[]
  counselingApproach?: ProviderFilter[]
  providerFeatureTags: ProviderFilter[]
  providerInsurances: ProviderInsurance[]
  LGBTQIAExperience: ProviderFilter[]
  languagesSpoken: ProviderFilter[]
}

export const getProviderSectionDetails = (
  providerDetails: Provider
): ProviderSectionDetailsType => {
  const genderPronouns = providerDetails?.filters?.filter(
    (filter) => filter.type === 'gender_pronoun'
  )

  const populationServed = providerDetails?.filters?.filter(
    (filter) => filter.type === 'counseling_type'
  )

  const practice = providerDetails?.practice
  const acceptsInsurance = !isEmpty(practice?.insurances)

  const location = practice ? `${practice.city}, ${practice.state}` : null
  const topSpecialities =
    providerDetails?.filters?.filter(
      (filter) => filter.type === 'area_of_concern' && filter.priority === 1
    ) ?? []
  const otherSpecialities =
    providerDetails?.filters?.filter(
      (filter) => filter.type === 'area_of_concern' && filter.priority === 2
    ) ?? []
  const availabilityTags = providerDetails?.filters?.filter(
    (filter) => filter.type === 'availability_type'
  )
  const agesServed = providerDetails?.filters?.filter(
    (filter) => filter.type === 'client_age_group'
  )
  const counselingApproach = providerDetails?.filters?.filter(
    (filter) => filter.type === 'counseling_approach'
  )
  const providerFeatureTags = providerDetails?.filters?.filter(
    (filter) => filter.type === 'tag'
  )

  const languagesSpoken =
    providerDetails?.filters?.filter((filter) => filter.type === 'language') ??
    []

  const LGBTQIAExperience = providerDetails?.filters?.filter((filter) =>
    filter.value?.toLowerCase().includes('lgbt')
  )

  const providerInsurances = providerDetails?.practice?.insurances

  return {
    genderPronouns,
    populationServed,
    acceptsInsurance,
    location,
    topSpecialities,
    otherSpecialities,
    availabilityTags,
    agesServed,
    counselingApproach,
    providerFeatureTags,
    providerInsurances,
    LGBTQIAExperience,
    languagesSpoken,
  }
}

export const getProviderTitle = (providerDetails: Provider): string => {
  const titleFilter = providerDetails?.filters?.find(
    (providerFilter) => providerFilter.type === 'provider_title'
  )
  return titleFilter?.value
}

export const formatClinicalSupervisorName = (
  supervisor: ClinicalSupervisor
): string =>
  `${supervisor.firstName.trim()} ${supervisor.lastName.trim()}, ${supervisor.title.trim()}`

//checks if a given text is a valid url
export const isValidHttpUrl = (text: string): boolean => {
  let url: URL

  try {
    url = new URL(text)
  } catch (error) {
    return false
  }

  return url.protocol === 'http:' || url.protocol === 'https:'
}

//removes http, https, or www from a url. Ex: https://www.youtube.com => youtube.com
export const formattedURL = (url: string): string => {
  return url.replace(/^(?:https?:\/\/)?(?:www\.)?/i, '').split('/')[0]
}

export const getCombinedAgeRangeFromProvider = (
  providerDetails: Provider
): string => {
  const combinedSortedAges = ageGroupServed(
    getAgeGroupsFromProvider(providerDetails)
  )
  return getCombinedAgeRange(combinedSortedAges)
}

export const getCombinedAgeRange = (ageGroups: number[][]): string => {
  //Following code is to combine the various consequitive age ranges to bigger
  //chunk of age range if possible.
  //For example, when the different age ranges are all of them right from 0-5 to 65+,
  //The function returns string '0-65+'
  //The input for this function should look like this: [[0,5], [6,10], [11,13], [14,17], [18,64], [65,299]]
  const endIndex = ageGroups?.length - 1

  //if the last two indices of combinedSortedAges are [18, 64], [65,299]] respectively
  //then combine them to [18,64]
  if (
    ageGroups?.[endIndex]?.[0] === 65 &&
    ageGroups?.[endIndex - 1]?.[1] === 64
  ) {
    ageGroups[endIndex - 1][1] = 65
    ageGroups?.pop()
  }

  const sortedAgeRange = ageGroups?.map((ageGroup) => {
    if (ageGroup?.[0] === 65) {
      return '65+'
    }

    let ageRange = ageGroup?.join('-')

    if (ageGroup?.[1] === 65) {
      ageRange += '+'
    }

    return ageRange
  })

  return sortedAgeRange?.join(', ')
}

export const getPreviousRouterUrlQuery = (): ParsedUrlQuery => {
  const previousRouter = JSON.parse(
    sessionStorage.getItem(sessionStorageKeys.PREVIOUS_ROUTER)
  )

  return previousRouter?.query || {}
}

export const getFilterValueFromId = (
  groupedFilters: GroupedProviderFilter,
  query: ParsedUrlQuery
): { [key: string]: string | string[] | number } | null => {
  const trackerInformation = {}

  Object.keys(providerFilterKeyMapping).forEach((providerFilterKey) => {
    const queryValue = query[providerFilterKey]

    if (queryValue) {
      const filterKeyMapped =
        FILTER_KEY_MAPPING[providerFilterKey.replace('[]', '')]
      const providerSection = groupedFilters[filterKeyMapped]

      if (providerSection) {
        const queryValueArray = Array.isArray(queryValue)
          ? queryValue
          : [queryValue]
        const selectedValues = providerSection
          .filter((filter) => queryValueArray.includes(filter.id))
          .map((filter) => filter.value)

        trackerInformation[filterKeyMapped] = selectedValues
      }
    }

    return null
  })

  return trackerInformation
}

/**
 * get all filters for this provider, and find the values
 * of any filter ids existing in the URL query
 */
export const queryFilters = (
  providerDetails: Provider,
  query: ParsedUrlQuery
): {
  [key: string]: string | string[] | number
} => {
  const filters = providerDetails?.filters

  if (filters) {
    const groupedFilters = groupFilters(filters)
    return getFilterValueFromId(groupedFilters, query)
  }

  return {}
}

/**
 * Structure data for tracker
 */
export const setTrackProviderInformation = (
  providerDetails: Provider
): {
  provider_name: string
  provider_id: string
  practice_state: string
  practice_city: string
} => {
  const { id, firstName, lastName, practice } = providerDetails

  return {
    provider_name: `${firstName} ${lastName}`,
    provider_id: id,
    practice_state: practice?.state,
    practice_city: practice?.city,
  }
}

/**
 * RudderStack Event that passes basic provider information
 * and any additional data you'd like to append
 * to the event of your choice
 */
export const providerRudderStackEvent = (
  eventEnum: RudderEvent,
  providerDetails: Provider,
  additionalTrackerInformation?: { [key: string]: string | string[] | number }
): void => {
  const trackProviderInformation = setTrackProviderInformation(providerDetails)

  logEvent(
    eventEnum,
    {
      ...trackProviderInformation,
      ...additionalTrackerInformation,
    },
    {
      [RudderstackIntegrations.GOOGLE_ADS]: true,
    }
  )
}

const buildPostalAddress = (
  address: string,
  city: string,
  state: string,
  zipcode: string
) => ({
  '@type': 'PostalAddress',
  streetAddress: address?.split(',')[0],
  addressLocality: city,
  addressRegion: state,
  postalCode: zipcode?.slice(0, 5),
  addressCountry: 'US',
})

const getImageEncodingFormat = (imageUrl: string): string | undefined => {
  try {
    const pathname = new URL(imageUrl).pathname
    const extension = pathname.split('.').pop()?.toLowerCase()

    return extension ? IMAGE_EXTENSION_TO_ENCODING[extension] : undefined
  } catch {
    return undefined
  }
}

const buildProviderPathImageObject = (
  profileImagePath: string,
  firstName: string,
  lastName: string,
  profileUrl: string
) => {
  const encodingFormat = getImageEncodingFormat(profileImagePath)

  return {
    '@type': 'ImageObject',
    '@id': `${profileUrl}#primaryimage`,
    contentUrl: profileImagePath,
    url: profileImagePath,
    name: `${firstName} ${lastName} headshot`,
    representativeOfPage: true,
    ...(encodingFormat && { encodingFormat }),
  }
}

const buildProviderPathNpiIdentifier = (about: ProviderAbout) => {
  if (!about?.npi_number) {
    return undefined
  }

  return {
    '@type': 'PropertyValue',
    propertyID: 'NPI',
    value: String(about.npi_number),
  }
}

const buildProviderPathCredentialValidIn = (practice: Provider['practice']) => {
  const stateCode = practice?.state

  if (!stateCode) {
    return undefined
  }

  const stateName =
    practice?.stateName ||
    stateAbbreviations[stateCode as keyof typeof stateAbbreviations] ||
    stateCode

  return {
    '@type': 'State',
    name: stateName,
    identifier: stateCode,
  }
}

const buildProviderPathCredentialNodes = (
  about: ProviderAbout,
  practice: Provider['practice'],
  profileUrl: string
): Record<string, unknown>[] => {
  // License only — certificates are not verified board credentials in our payload.
  // We also do not have verified information about which organization
  // recognizes this license (no board name/URL on the profile payload).
  const licenseNumber = about?.license_number

  if (!licenseNumber) {
    return []
  }

  const validIn = buildProviderPathCredentialValidIn(practice)

  return [
    {
      '@type': 'EducationalOccupationalCredential',
      '@id': `${profileUrl}#credential-license`,
      credentialCategory: 'license',
      ...(validIn && { validIn }),
      identifier: {
        '@type': 'PropertyValue',
        propertyID: 'License Number',
        value: String(licenseNumber),
      },
    },
  ]
}

const resolveProviderPathPronouns = (providerDetails: Provider): string => {
  const fromPronouns = formatProviderPronouns(providerDetails?.pronouns)

  if (fromPronouns) {
    return fromPronouns
  }

  const genderPronouns = getProviderSectionDetails(providerDetails)
    .genderPronouns?.map((filter) => filter.value)
    .filter(Boolean)

  return formatProviderPronouns(genderPronouns)
}

const buildProviderPathNestedReviews = (
  testimonials: ProviderTestimonials
): Record<string, unknown>[] => {
  return testimonials.items.map((item) => {
    const authorName = item.authorFirstName
      ? sanitizeTextForJsonLd(item.authorFirstName)
      : ''
    const safeAuthorName = authorName || DEFAULT_TESTIMONIAL_AUTHOR

    const reviewNode: Record<string, unknown> = {
      '@type': 'Review',
      reviewBody: sanitizeTextForJsonLd(item.reviewBody),
      author: {
        '@type': 'Person',
        name: safeAuthorName,
      },
    }

    if (item.datePublished) {
      reviewNode.datePublished = item.datePublished
    }

    if (item.rating != null) {
      reviewNode.reviewRating = {
        '@type': 'Rating',
        ratingValue: item.rating,
        bestRating: TESTIMONIAL_RATING_SCALE,
      }
    }

    return reviewNode
  })
}

/**
 * Standalone LocalBusiness reviews schema (separate ld+json script, as before).
 * Not placed in ProfilePage @graph — LocalBusiness is invalid for hasPart.
 */
const buildProviderPathLocalBusinessReviewsSchema = (
  testimonials: ProviderTestimonials,
  profileUrl: string,
  fullName: string,
  postalAddress: Record<string, unknown>,
  imageObject: Record<string, unknown>
): Record<string, unknown> | undefined => {
  if (!testimonials?.items?.length) {
    return undefined
  }

  const review = buildProviderPathNestedReviews(testimonials)
  const { summary } = testimonials
  const localBusinessSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@type': 'LocalBusiness',
    '@id': `${profileUrl}#reviews`,
    url: profileUrl,
    name: fullName,
    image: imageObject,
    address: postalAddress,
    contactPoint: {
      '@type': 'ContactPoint',
      telephone: providerProfileNumberWithCode,
      contactType: 'Work',
    },
    review,
  }

  if (summary?.reviewCount > 0 && summary.averageRating != null) {
    localBusinessSchema.aggregateRating = {
      '@type': 'AggregateRating',
      ratingValue: summary.averageRating,
      reviewCount: summary.reviewCount,
      bestRating: String(TESTIMONIAL_RATING_SCALE),
    }
  }

  return localBusinessSchema
}

/** @graph JSON-LD for provider profile pages (`/provider/`, `/therapist/`, `/psychiatrist/`). */
export const generateProviderLdJsonSchema = (
  providerDetails: Provider,
  profileUrl: string
): LdJsonSchema => {
  const {
    firstName,
    lastName,
    practice,
    filters,
    profileImagePath,
    shortIntro,
    about,
    faq,
    testimonials,
  } = providerDetails ?? {}

  const { address, city, state, zipcode } = practice ?? {}
  const {
    topSpecialities,
    otherSpecialities,
    languagesSpoken,
  } = getProviderSectionDetails(providerDetails)

  const resolvedProfileImagePath = profileImagePath || DEFAULT_PROVIDER_IMAGE
  const providerProfilePageUrl = profileUrl
  const webpageId = `${providerProfilePageUrl}#webpage`
  const personId = `${providerProfilePageUrl}#person`
  const primaryImageId = `${providerProfilePageUrl}#primaryimage`
  const fullName = `${firstName} ${lastName}`

  const titleAbbreviation = getProviderTitle(providerDetails)
  const jobTitle =
    credentials[titleAbbreviation]?.definition ?? titleAbbreviation

  const knowsAbout = [
    ...(topSpecialities ?? []).map((s) => s.value),
    ...(otherSpecialities ?? []).map((s) => s.value),
    ...(
      getDataFromProviderFilters(
        filters,
        providerGlobalFilters.CounselingApproach
      ) ?? []
    ).map((s) => s.value),
  ].filter(Boolean)

  const knowsLanguage = buildProviderPathKnowsLanguage(languagesSpoken)

  const pronouns = resolveProviderPathPronouns(providerDetails)
  const npiIdentifier = buildProviderPathNpiIdentifier(about)
  const postalAddress = buildPostalAddress(address, city, state, zipcode)
  const imageObject = buildProviderPathImageObject(
    resolvedProfileImagePath,
    firstName,
    lastName,
    providerProfilePageUrl
  )
  const credentialNodes = buildProviderPathCredentialNodes(
    about,
    practice,
    providerProfilePageUrl
  )
  const localBusinessReviewsSchema = testimonials
    ? buildProviderPathLocalBusinessReviewsSchema(
        testimonials,
        providerProfilePageUrl,
        fullName,
        postalAddress,
        imageObject
      )
    : undefined

  const faqId = `${providerProfilePageUrl}#faq`
  const faqMainEntity =
    faq?.map(({ question, answer }) => ({
      '@type': 'Question',
      name: sanitizeTextForJsonLd(question),
      acceptedAnswer: {
        '@type': 'Answer',
        text: sanitizeTextForJsonLd(answer),
      },
    })) ?? []
  const faqNode =
    faqMainEntity.length > 0
      ? {
          '@type': 'FAQPage',
          '@id': faqId,
          isPartOf: {
            '@id': webpageId,
          },
          mainEntity: faqMainEntity,
        }
      : undefined

  const profilePageNode: Record<string, unknown> = {
    '@type': 'ProfilePage',
    '@id': webpageId,
    url: providerProfilePageUrl,
    name: fullName,
    description: sanitizeTextForJsonLd(shortIntro || ''),
    isPartOf: {
      '@type': 'WebSite',
      '@id': THRIVEWORKS_SCHEMA_WEBSITE_ID,
    },
    publisher: {
      '@id': THRIVEWORKS_SCHEMA_ORGANIZATION_ID,
    },
    mainEntity: {
      '@id': personId,
    },
    primaryImageOfPage: {
      '@id': primaryImageId,
    },
    // hasPart expects CreativeWork (e.g. FAQPage); LocalBusiness is not valid here.
    ...(faqNode && {
      hasPart: {
        '@id': faqId,
      },
    }),
    inLanguage: 'en-US',
  }

  const personNode: Record<string, unknown> = {
    '@type': 'Person',
    '@id': personId,
    name: fullName,
    url: providerProfilePageUrl,
    image: {
      '@id': primaryImageId,
    },
    description: sanitizeTextForJsonLd(shortIntro || ''),
    ...(jobTitle && { jobTitle }),
    ...(titleAbbreviation && { honorificSuffix: [titleAbbreviation] }),
    affiliation: {
      '@type': 'Organization',
      '@id': THRIVEWORKS_SCHEMA_ORGANIZATION_ID,
    },
    mainEntityOfPage: {
      '@id': webpageId,
    },
    ...(pronouns && { pronouns }),
    ...(knowsLanguage.length > 0 && { knowsLanguage }),
    ...(knowsAbout.length > 0 && { knowsAbout }),
    ...(credentialNodes.length > 0 && {
      hasCredential: credentialNodes.map((credential) => ({
        '@id': credential['@id'],
      })),
    }),
    ...(npiIdentifier && { identifier: npiIdentifier }),
    address: postalAddress,
  }

  const profileGraphSchema: Record<string, unknown> = {
    '@context': 'https://schema.org',
    '@graph': [
      profilePageNode,
      personNode,
      imageObject,
      ...credentialNodes,
      ...(faqNode ? [faqNode] : []),
    ],
  }

  // HeadComponent emits one <script> per array entry — LocalBusiness stays separate.
  if (localBusinessReviewsSchema) {
    return [profileGraphSchema, localBusinessReviewsSchema]
  }

  return profileGraphSchema
}

export interface GroupedSimilarProviders {
  practiceLocationMatches: ProviderMetadataForPractice[]
  nonPracticeLocationMatches: ProviderMetadataForPractice[]
}

/**
 * Get selected area of concern IDs from URL query parameters
 */
export const getSelectedAreaOfConcernIds = (
  query: ParsedUrlQuery
): string[] => {
  // Support both bracketed and non-bracketed keys depending on parser
  const areaOfConcernParamWithBrackets = query['areaOfConcern[]']
  const areaOfConcernParamWithoutBrackets = ((query as unknown) as Record<
    string,
    unknown
  >)?.['areaOfConcern']

  const value = (areaOfConcernParamWithBrackets ??
    areaOfConcernParamWithoutBrackets) as string | string[] | undefined

  if (!value) {
    return []
  }

  return Array.isArray(value) ? value : [value]
}

/**
 * Sort specialties to prioritize selected ones from URL query
 */
export const sortSpecialtiesBySelection = (
  specialties: ProviderFilter[],
  selectedIds: string[]
): ProviderFilter[] => {
  if (!selectedIds.length || !specialties.length) {
    return specialties
  }

  const selectedSpecialties: ProviderFilter[] = []
  const remainingSpecialties: ProviderFilter[] = []

  // Separate selected and remaining specialties
  specialties.forEach((specialty) => {
    if (selectedIds.includes(specialty.id)) {
      selectedSpecialties.push(specialty)
    } else {
      remainingSpecialties.push(specialty)
    }
  })

  // Sort selected specialties by the order they appear in the URL
  const orderedSelectedSpecialties = selectedIds
    .map((id) => selectedSpecialties.find((s) => s.id === id))
    .filter(Boolean)

  return [...orderedSelectedSpecialties, ...remainingSpecialties]
}

export function filterAndSortGroupedSimilarProviders(
  providerDetails: Provider,
  otherProvidersFromPractice: ProviderMetadataForPractice[],
  allProvidersInState?: ProviderMetadataForPractice[]
): GroupedSimilarProviders {
  const {
    otherSpecialities,
    topSpecialities,
    populationServed,
    providerInsurances,
  } = getProviderSectionDetails(providerDetails)

  const counselingTypeIds = populationServed
    ?.map((population) => population?.id)
    .filter((id) => id != null)
  const specialitiesIds = [...topSpecialities, ...otherSpecialities]
    .map((speciality) => speciality?.id)
    .filter((id) => id != null)
  const insurancesIds = providerInsurances
    ?.map(
      (insurance) =>
        insurance?.insuranceProviderId || insurance?.insuranceProvider?.id
    )
    .filter((id) => id != null)

  const getSimilarProvidersFilter = (
    provider: ProviderMetadataForPractice,
    shouldUseSameLocation = true
  ) => {
    const hasMatchingServiceType =
      counselingTypeIds?.length > 0 &&
      provider?.counselingTypeIds?.some(
        (id) => id != null && counselingTypeIds.includes(id)
      )

    const isSameLocation = provider.practiceId === providerDetails.practice.id

    return (
      provider.id !== providerDetails.id &&
      provider.isAvailable &&
      (shouldUseSameLocation ? isSameLocation : !isSameLocation) &&
      hasMatchingServiceType
    )
  }

  /**
   * Calculate a weighted match score for a provider based on specialty and insurance matches.
   * Higher scores indicate better matches.
   */
  const getProviderMatchScore = (
    provider: ProviderMetadataForPractice
  ): number => {
    const topSpecialtyCount =
      provider?.topAreaOfConcernIds?.filter(
        (id) => id != null && specialitiesIds?.includes(id)
      ).length || 0
    const otherSpecialtyCount =
      provider?.areaOfConcernIds?.filter(
        (id) =>
          id != null &&
          specialitiesIds?.includes(id) &&
          !provider?.topAreaOfConcernIds?.includes(id)
      ).length || 0
    const insuranceCount =
      provider?.insuranceProviderIds?.filter(
        (id) => id != null && insurancesIds?.includes(id)
      ).length || 0

    // Primary sort by Top Specialty count (highest weight)
    // Secondary sort by Other Specialty count
    // Tertiary sort by Insurance count
    return topSpecialtyCount * 100 + otherSpecialtyCount * 10 + insuranceCount
  }

  // Practice location matches:
  // Same practice location + Service type (at least 1)
  const practiceLocationMatchesFiltered = otherProvidersFromPractice.filter(
    (p) => getSimilarProvidersFilter(p)
  )

  // Sort practice location matches by Top Specialty match count priority
  const practiceLocationMatches = practiceLocationMatchesFiltered.sort(
    (a, b) => getProviderMatchScore(b) - getProviderMatchScore(a)
  )

  // Non practice location matches:
  // Same state + Service type (at least 1)
  const nonPracticeLocationMatches = (allProvidersInState || []).filter((p) =>
    getSimilarProvidersFilter(p, false)
  )

  // Sort non-practice location matches by Top Specialty match count priority
  const sortedNonPracticeMatches = nonPracticeLocationMatches.sort(
    (a, b) => getProviderMatchScore(b) - getProviderMatchScore(a)
  )

  return {
    practiceLocationMatches: practiceLocationMatches.slice(0, 6), // Limit to 6 providers per group
    nonPracticeLocationMatches: sortedNonPracticeMatches.slice(0, 6),
  }
}

export type FeaturedTestimonialPropsFromProvider = {
  hasItems: boolean
  hasFeatured: boolean
  featuredQuote: string | null
  hasMultipleTestimonials: boolean
  attribution: string | null
  testimonialCount: number
}

export function getFeaturedTestimonialProps(
  providerDetails: Provider | null | undefined
): FeaturedTestimonialPropsFromProvider {
  const items = (providerDetails?.testimonials?.items ?? []).filter(
    (testimonial) => (testimonial.reviewBody ?? '').trim().length > 0
  )
  const featured = providerDetails?.testimonials?.featuredTestimonial ?? null
  const hasFeatured =
    !!featured && (featured.reviewBody ?? '').trim().length > 0
  const authorName = featured?.authorFirstName?.trim() || 'A Thriveworks Client'
  const providerFirstName = providerDetails?.firstName?.trim()

  let attribution: string | null = null

  if (hasFeatured) {
    if (providerFirstName) {
      attribution = `${authorName}, verified patient of ${providerFirstName}`
    } else {
      attribution = authorName
    }
  }

  return {
    hasItems: items.length > 0,
    hasFeatured,
    featuredQuote: hasFeatured ? featured.reviewBody?.trim() ?? null : null,
    hasMultipleTestimonials:
      (providerDetails?.testimonials?.summary?.reviewCount ?? items.length) > 1,
    attribution,
    testimonialCount:
      providerDetails?.testimonials?.summary?.reviewCount ?? items.length,
  }
}

export function getTestimonialAttribution(
  item: ProviderTestimonialItem,
  providerFirstName: string
): string {
  const name = item.authorFirstName?.trim() || 'A Thriveworks Client'
  const trimmed = providerFirstName?.trim()
  return trimmed ? `${name}, verified patient of ${trimmed}` : name
}

/**
 * Get the full state name from state abbreviation
 */
export const getFullStateName = (stateAbbreviation: string): string => {
  const key = stateAbbreviation?.toUpperCase() as keyof typeof stateAbbreviations
  return stateAbbreviations[key] || stateAbbreviation || ''
}

/**
 * Determine the session offering type based on provider's availability filters
 */
export const getSessionOfferingType = (
  providerDetails: Provider
): SessionOfferingType => {
  const availabilityFilters =
    providerDetails?.filters?.filter(
      (filter) => filter.type === 'availability_type'
    ) ?? []

  const hasOnline = availabilityFilters.some(
    (filter) => filter.value?.toLowerCase() === 'online'
  )
  const hasInPerson = availabilityFilters.some(
    (filter) => filter.value?.toLowerCase() === 'in person'
  )

  if (hasOnline && hasInPerson) {
    return SessionOfferingType.BOTH
  } else if (hasOnline) {
    return SessionOfferingType.ONLINE_ONLY
  } else if (hasInPerson) {
    return SessionOfferingType.IN_PERSON_ONLY
  }

  return SessionOfferingType.NO_SCHEDULE
}

/**
 * Psychiatry vs therapy for SEO copy — aligned with profile URL classification
 * (`getPractitionerType`) and `Provider.isPsychiatry` when the API sets it.
 */
export const getProviderServiceType = (
  providerDetails: Provider
): ProviderServiceType => {
  if (providerDetails?.isPsychiatry === true) {
    return ProviderServiceType.PSYCHIATRY
  }

  const providerTitle = getProviderTitle(providerDetails)
  const practitionerType = getPractitionerType(providerTitle)

  if (
    practitionerType === practitionerTypes.PROVIDER ||
    practitionerType === practitionerTypes.PSYCHIATRIST
  ) {
    return ProviderServiceType.PSYCHIATRY
  }

  return ProviderServiceType.THERAPY
}

/**
 * Get user-friendly credential name from abbreviation
 */
export const getUserFriendlyCredential = (credential: string): string => {
  const credentialData = credentials[credential]
  return credentialData?.definition || credential || ''
}

/**
 * Generate SEO-optimized meta title and description for provider profile page
 * Based on 8 template conditions:
 * 1. NP + in-person only
 * 2. NP + both online and in-person
 * 3. Talk Therapy + in-person only
 * 4. Talk Therapy + both online and in-person
 * 5. NP + online only
 * 6. Talk Therapy + online only
 * 7. Talk Therapy + no schedule (shows "therapy")
 * 8. NP + no schedule (shows "psychiatry")
 */
export const generateProviderMetaTags = (
  providerDetails: Provider
): ProviderMetaTags => {
  const { firstName, lastName, practice } = providerDetails ?? {}
  const { city, state, stateName } = practice ?? {}

  const providerTitle = getProviderTitle(providerDetails)
  const sessionType = getSessionOfferingType(providerDetails)
  const serviceType = getProviderServiceType(providerDetails)
  const userFriendlyCredential = getUserFriendlyCredential(providerTitle)
  const fullStateName = stateName || getFullStateName(state)

  const fullName = `${firstName?.trim() || ''} ${lastName?.trim() || ''}`.trim()
  const credential = providerTitle || ''

  let title: string
  let description: string

  if (sessionType === SessionOfferingType.NO_SCHEDULE) {
    title = `${fullName}, ${credential} in ${fullStateName}`
    description = `${fullName} is a ${userFriendlyCredential} offering ${serviceType} in ${fullStateName}. Insurance accepted. View their availability or explore all providers in ${fullStateName}.`
  } else if (sessionType === SessionOfferingType.ONLINE_ONLY) {
    title = `${fullName}, ${credential} in ${fullStateName}`
    description = `${fullName} is a ${userFriendlyCredential} offering online ${serviceType} in ${fullStateName}. Insurance accepted. View their availability or explore all providers in ${fullStateName}.`
  } else {
    title = `${fullName}, ${credential} in ${city}, ${state}`

    if (sessionType === SessionOfferingType.IN_PERSON_ONLY) {
      description = `${fullName} is a ${userFriendlyCredential} offering in-person ${serviceType} in ${city}, ${state}. Insurance accepted. View their availability or explore all providers in ${fullStateName}.`
    } else {
      description = `${fullName} is a ${userFriendlyCredential} offering online or in-person ${serviceType} in ${city}, ${state}. Insurance accepted. View their availability or explore all providers in ${fullStateName}.`
    }
  }

  return { title, description }
}

type FullscreenVideoElement = HTMLVideoElement & {
  webkitEnterFullscreen?: () => void
}

type NativeVideoHandlers = {
  onStarted?: () => void
  onProgress?: (currentTime: number, duration: number) => void
  onSeek?: () => void
  onEnded?: (duration: number) => void
}

// Mobile-only: plays a provider intro video directly in the browser's native
// fullscreen video player, skipping the image/video lightbox modal entirely.
// The fullscreen call must run synchronously within the click handler that
// invokes this function - awaiting anything first can drop the user-gesture
// context Safari/Chrome require to allow fullscreen.
export const playVideoNatively = (
  videoUrl: string,
  handlers?: NativeVideoHandlers
): void => {
  const video = document.createElement('video') as FullscreenVideoElement
  video.src = videoUrl
  video.controls = true
  video.playsInline = false
  video.style.position = 'fixed'
  video.style.top = '-9999px'

  const removeVideo = () => {
    video.remove()
  }

  // 'playing' only fires once frames are actually rendering, so a tap that
  // never gets playback going (blocked/failed play) doesn't count as a start.
  video.addEventListener('playing', () => handlers?.onStarted?.(), {
    once: true,
  })
  video.addEventListener('timeupdate', () =>
    handlers?.onProgress?.(video.currentTime, video.duration)
  )
  video.addEventListener('seeking', () => handlers?.onSeek?.())
  video.addEventListener('ended', () => {
    handlers?.onEnded?.(video.duration)
    removeVideo()
  })
  video.addEventListener('webkitendfullscreen', removeVideo)
  video.addEventListener('fullscreenchange', () => {
    if (!document.fullscreenElement) {
      removeVideo()
    }
  })

  document.body.appendChild(video)
  video.play().catch(removeVideo)

  if (video.webkitEnterFullscreen) {
    video.webkitEnterFullscreen()
  } else if (video.requestFullscreen) {
    video.requestFullscreen().catch(removeVideo)
  }
}
